import { create } from 'zustand';
import { stockService } from '../services';
import type {
  ItemStockDto,
  ItemStockSummaryDto,
  StockStatus,
  StockCacheEntry,
  StockSummaryCacheEntry,
} from '../types';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';

const DEBOUNCE_MS = 1500; // 1.5 seconds
const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Two reads, deliberately separate.
 *
 * **Summary** (`summaryCache`) — totals per item with a per-bin breakdown. Fetched in a debounced
 * batch for every selected item, and the source for every availability display: the badge, the
 * max-quantity clamp, the cart warnings, the submit gates.
 *
 * **Detail** (`detailCache`) — one entry per bin PER SERIAL. Fetched lazily for ONE item at the
 * moment its serials are actually needed (opening the bin allocation dialog), because for a
 * serial-tracked item it returns one row per unit on hand — 1,152 rows for a single plastic box.
 * Pulling that for every selected item to draw a badge is what made a 1,286-row request out of a
 * four-item screen, and while the server read was unpaginated it silently exceeded NetSuite's
 * 1,000-row page cap: one item ate the page and the rest rendered "Out of Stock" against real stock.
 */
/**
 * The badge status from values the caller already holds. `getStockStatus` reads the store inside the
 * call, which the React Compiler cannot see as a dependency: a component that calls it in render gets
 * a memoised, stale badge (86d3d1z40). A component subscribes to the entry, loading and error for its
 * item and passes them here instead.
 */
export function stockStatusOf(
  entry: StockSummaryCacheEntry | undefined,
  requestedQty: number,
  loading: boolean,
  errored: boolean,
): StockStatus {
  if (loading) return 'loading';
  if (errored) return 'error';
  if (!entry) return 'unknown';
  const available = entry.data.quantityAvailable;
  if (available <= 0) return 'out_of_stock';
  if (available < requestedQty) return 'low';
  return 'available';
}

interface StockState {
  // Summary cache keyed by netsuiteItemId (as string for object key)
  summaryCache: Record<string, StockSummaryCacheEntry>;
  // Serial-level cache, keyed the same way. Only populated on demand.
  detailCache: Record<string, StockCacheEntry>;
  // Set of netsuiteItemIds currently being fetched (summary)
  loadingItems: Set<number>;
  // Set of netsuiteItemIds whose serial-level detail is in flight
  loadingDetailItems: Set<number>;
  // Error messages per netsuiteItemId
  errorItems: Record<string, string>;
  detailErrorItems: Record<string, string>;

  // Internal: pending items to fetch (for debounce batching)
  _pendingItemIds: Set<number>;
  _debounceTimer: ReturnType<typeof setTimeout> | null;

  // Actions
  queueStockFetch: (itemIds: number[]) => void;
  fetchStockBatch: (itemIds: number[]) => Promise<void>;
  refreshStock: (itemIds: number[]) => Promise<void>;
  fetchStockDetail: (netsuiteItemId: number) => Promise<void>;
  refreshStockDetail: (netsuiteItemId: number) => Promise<void>;
  clearCache: () => void;

  // Selectors
  getStock: (netsuiteItemId: number) => ItemStockSummaryDto | null;
  getStockDetail: (netsuiteItemId: number) => ItemStockDto | null;
  getStockStatus: (netsuiteItemId: number, requestedQty: number) => StockStatus;
  isLoading: (netsuiteItemId: number) => boolean;
  isDetailLoading: (netsuiteItemId: number) => boolean;
  getError: (netsuiteItemId: number) => string | null;
  getDetailError: (netsuiteItemId: number) => string | null;
}

export const useStockStore = create<StockState>()((set, get) => ({
  summaryCache: {},
  detailCache: {},
  loadingItems: new Set(),
  loadingDetailItems: new Set(),
  errorItems: {},
  detailErrorItems: {},
  _pendingItemIds: new Set(),
  _debounceTimer: null,

  /**
   * Queue items for stock fetch with debounce batching.
   * Multiple rapid calls will be batched into a single API request.
   */
  queueStockFetch: (itemIds: number[]) => {
    const state = get();
    const now = Date.now();

    // Filter out items that are already loading or fresh in cache
    const needsFetch = itemIds.filter((id) => {
      if (state.loadingItems.has(id)) return false;
      if (state._pendingItemIds.has(id)) return false;

      const cached = state.summaryCache[String(id)];
      if (cached && now - cached.fetchedAt < STALE_TIME_MS) {
        return false;
      }

      return true;
    });

    if (needsFetch.length === 0) return;

    // Add to pending set
    const newPending = new Set(state._pendingItemIds);
    needsFetch.forEach((id) => newPending.add(id));

    // Clear existing timer
    if (state._debounceTimer) {
      clearTimeout(state._debounceTimer);
    }

    // Set new debounce timer
    const timer = setTimeout(() => {
      const currentState = get();
      const idsToFetch = Array.from(currentState._pendingItemIds);

      // Clear pending
      set({ _pendingItemIds: new Set(), _debounceTimer: null });

      if (idsToFetch.length > 0) {
        currentState.fetchStockBatch(idsToFetch);
      }
    }, DEBOUNCE_MS);

    set({ _pendingItemIds: newPending, _debounceTimer: timer });
  },

  /**
   * Fetch stock summaries for a batch of items (internal, called after debounce)
   */
  fetchStockBatch: async (itemIds: number[]) => {
    if (itemIds.length === 0) return;

    const state = get();

    // Mark as loading
    const newLoading = new Set(state.loadingItems);
    itemIds.forEach((id) => newLoading.add(id));

    // Clear previous errors for these items
    const newErrors = { ...state.errorItems };
    itemIds.forEach((id) => delete newErrors[String(id)]);

    set({ loadingItems: newLoading, errorItems: newErrors });

    try {
      const results = await stockService.getStockSummaryBatch(itemIds);

      // Update cache with results
      const newCache = { ...get().summaryCache };
      const now = Date.now();

      results.forEach((stock) => {
        newCache[String(stock.netsuiteItemId)] = {
          data: stock,
          fetchedAt: now,
        };
      });

      // An item the server did not answer for is NOT zero stock.
      //
      // This used to fabricate a zero-stock entry here, which turned "we never heard about this
      // item" into a confident "Out of Stock" — and that is exactly how the 1,000-row truncation
      // stayed invisible: the rows were dropped server-side and the client asserted the conclusion.
      // The server now returns an explicit zero for items NetSuite genuinely does not know, so
      // anything still missing means the answer did not arrive. Record it as an error, which is
      // recoverable (it blocks submission and offers a retry) rather than quietly wrong.
      const returnedIds = new Set(results.map((r) => r.netsuiteItemId));
      const errorItems = { ...get().errorItems };
      itemIds.forEach((id) => {
        if (!returnedIds.has(id)) {
          errorItems[String(id)] = 'Stock data missing from the server response';
        }
      });

      // Remove from loading
      const updatedLoading = new Set(get().loadingItems);
      itemIds.forEach((id) => updatedLoading.delete(id));

      set({ summaryCache: newCache, loadingItems: updatedLoading, errorItems });
    } catch (error) {
      const errorMsg = getApiErrorMessage(error, 'Failed to fetch stock');

      // Mark items as errored
      const errorItems = { ...get().errorItems };
      itemIds.forEach((id) => {
        errorItems[String(id)] = errorMsg;
      });

      // Remove from loading
      const updatedLoading = new Set(get().loadingItems);
      itemIds.forEach((id) => updatedLoading.delete(id));

      set({ errorItems, loadingItems: updatedLoading });
    }
  },

  /**
   * Force refresh stock for specific items (clears cache first).
   * This is the retry path behind an `error` badge — the submit gate blocks on `error`, so a user
   * must be able to clear it without reloading the page.
   */
  refreshStock: async (itemIds: number[]) => {
    // Clear cache for these items
    const newCache = { ...get().summaryCache };
    itemIds.forEach((id) => delete newCache[String(id)]);
    set({ summaryCache: newCache });

    // Fetch immediately (bypass debounce)
    return get().fetchStockBatch(itemIds);
  },

  /**
   * Fetch SERIAL-LEVEL stock for a single item. Call this when the serials themselves are needed —
   * i.e. when opening the bin allocation dialog — not for a screen of items up front. No-ops if the
   * item is already loading or cached fresh, so re-opening a dialog costs nothing.
   */
  fetchStockDetail: async (netsuiteItemId: number) => {
    const state = get();
    const key = String(netsuiteItemId);

    if (state.loadingDetailItems.has(netsuiteItemId)) return;

    const cached = state.detailCache[key];
    if (cached && Date.now() - cached.fetchedAt < STALE_TIME_MS) return;

    const newLoading = new Set(state.loadingDetailItems);
    newLoading.add(netsuiteItemId);
    const clearedErrors = { ...state.detailErrorItems };
    delete clearedErrors[key];
    set({ loadingDetailItems: newLoading, detailErrorItems: clearedErrors });

    try {
      const results = await stockService.getStockBatch([netsuiteItemId]);
      const match = results.find((r) => r.netsuiteItemId === netsuiteItemId) ?? null;

      const updatedLoading = new Set(get().loadingDetailItems);
      updatedLoading.delete(netsuiteItemId);

      if (!match) {
        // Same rule as the summary read: absent is not empty.
        set({
          loadingDetailItems: updatedLoading,
          detailErrorItems: {
            ...get().detailErrorItems,
            [key]: 'Bin data missing from the server response',
          },
        });
        return;
      }

      set({
        detailCache: { ...get().detailCache, [key]: { data: match, fetchedAt: Date.now() } },
        loadingDetailItems: updatedLoading,
      });
    } catch (error) {
      const updatedLoading = new Set(get().loadingDetailItems);
      updatedLoading.delete(netsuiteItemId);
      set({
        loadingDetailItems: updatedLoading,
        detailErrorItems: {
          ...get().detailErrorItems,
          [key]: getApiErrorMessage(error, 'Failed to load bins'),
        },
      });
    }
  },

  /** Retry path for the bin dialog — drops the cached entry so the fetch actually re-runs. */
  refreshStockDetail: async (netsuiteItemId: number) => {
    const newCache = { ...get().detailCache };
    delete newCache[String(netsuiteItemId)];
    set({ detailCache: newCache });
    return get().fetchStockDetail(netsuiteItemId);
  },

  /**
   * Clear all cached stock data
   */
  clearCache: () => {
    set({
      summaryCache: {},
      detailCache: {},
      errorItems: {},
      detailErrorItems: {},
      loadingItems: new Set(),
      loadingDetailItems: new Set(),
    });
  },

  // Selectors
  getStock: (netsuiteItemId: number): ItemStockSummaryDto | null => {
    return get().summaryCache[String(netsuiteItemId)]?.data ?? null;
  },

  getStockDetail: (netsuiteItemId: number): ItemStockDto | null => {
    return get().detailCache[String(netsuiteItemId)]?.data ?? null;
  },

  getStockStatus: (netsuiteItemId: number, requestedQty: number): StockStatus => {
    const state = get();
    return stockStatusOf(
      state.summaryCache[String(netsuiteItemId)],
      requestedQty,
      state.loadingItems.has(netsuiteItemId) || state._pendingItemIds.has(netsuiteItemId),
      !!state.errorItems[String(netsuiteItemId)],
    );
  },

  isLoading: (netsuiteItemId: number): boolean => {
    const state = get();
    return state.loadingItems.has(netsuiteItemId) || state._pendingItemIds.has(netsuiteItemId);
  },

  isDetailLoading: (netsuiteItemId: number): boolean => {
    return get().loadingDetailItems.has(netsuiteItemId);
  },

  getError: (netsuiteItemId: number): string | null => {
    return get().errorItems[String(netsuiteItemId)] ?? null;
  },

  getDetailError: (netsuiteItemId: number): string | null => {
    return get().detailErrorItems[String(netsuiteItemId)] ?? null;
  },
}));
